import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  HStack,
  VStack,
} from '@chakra-ui/react';
import { useNavigation } from '../components/Navigation';

import { Icon, IconName } from './uiold';
import { getRoutes, RoutePath } from '../components/Navigation/routes';
import {
  PageRouteDescriptor,
  RouteDescriptor,
} from '../components/Navigation/types';
import { useAuthState } from './AuthState';
import { Account } from '../types/shared/Account';

type CommandPaletteRoute = Pick<PageRouteDescriptor, 'name' | 'icon'> & {
  path: RoutePath;
  parent?: string;
  system_admin: boolean;
};

function getCommandPaletteRoutes(account: Account): Array<CommandPaletteRoute> {
  const isUserAdmin = account.system_admin ?? false;

  const routesArray: Array<CommandPaletteRoute> = [];
  const addRoutes = (
    routes: ReadonlyArray<RouteDescriptor>,
    navPath: Array<string> = [],
  ) => {
    for (const route of routes) {
      if (route.type === 'page') {
        const allowed = !route.system_admin || isUserAdmin;
        if (
          allowed &&
          !route.disabled &&
          !route.path.includes(':') &&
          !route.alias
        ) {
          const breadcrumbs = navPath.join(' > ') || undefined;
          routesArray.push({
            path: route.path as RoutePath,
            name: route.name,
            icon: route.icon,
            parent: breadcrumbs,
            system_admin: route.system_admin ?? false,
          });
        }
      } else {
        addRoutes(route.routes, [...navPath, route.name]);
      }
    }
  };
  addRoutes(getRoutes(account));
  return routesArray;
}

export default function CommandPalette() {
  const [isOpen, setOpen] = useState(false);

  useEffect(() => {
    return addKeyDownListener((event) => {
      if (!isOpen && (event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        setOpen(true);
      }
      if (isOpen && event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        setOpen(false);
      }
    });
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      closeOnEsc={false}
      onClose={() => {
        setOpen(false);
      }}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <CommandPaletteContent
        onClose={() => {
          setOpen(false);
        }}
      />
    </Modal>
  );
}

function CommandPaletteContent(props: { onClose: () => void }) {
  const { onClose } = props;
  const { navigate } = useNavigation();
  const [search, setSearch] = useState('');
  const { account } = useAuthState();

  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);

  const routes = useMemo(() => getCommandPaletteRoutes(account), [account]);

  const filteredRoutes = useMemo(() => {
    const searchString = search.toLowerCase();
    return routes.filter(
      (route) =>
        route.name.toLowerCase().includes(searchString) ||
        route.parent?.toLowerCase().includes(searchString),
    );
  }, [search, routes]);

  const chooseRoute = (path: string) => {
    navigate(path as never, {} as never);
    onClose();
  };

  useEffect(() => {
    // If the selected index is out of bounds (because the filteredRoutes
    // changed) then set it to a valid value.
    setSelectedRouteIndex((selectedRouteIndex) => {
      let maxValidIndex = Math.max(0, filteredRoutes.length - 1);
      return Math.min(selectedRouteIndex, maxValidIndex);
    });
    const onEnter = () => {
      const selectedRoute = filteredRoutes[selectedRouteIndex];
      if (selectedRoute) {
        chooseRoute(selectedRoute.path);
      }
    };
    return addKeyDownListener((event) => {
      if (event.key === 'ArrowUp') {
        setSelectedRouteIndex((i) => Math.max(0, i - 1));
      }
      if (event.key === 'ArrowDown') {
        setSelectedRouteIndex((i) =>
          Math.min(filteredRoutes.length - 1, i + 1),
        );
      }
      if (event.key === 'Enter') {
        onEnter();
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredRoutes, selectedRouteIndex]);

  return (
    <ModalContent>
      <ModalHeader>
        <InputGroup>
          <InputLeftElement
            pointerEvents="none"
            children={<Icon name="search" size="L" />}
          />
          <Input
            my="2"
            size="lg"
            variant="unstyled"
            placeholder={t('Search MarketWurks')}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
      </ModalHeader>
      {search ? (
        <ModalBody>
          {filteredRoutes.map((route, index) => (
            <VStack
              key={index}
              rounded={6}
              bg={index === selectedRouteIndex ? 'blue.700' : 'gray.100'}
              p={3}
              my={3}
              textColor={index === selectedRouteIndex ? 'white' : undefined}
              style={{ cursor: 'pointer' }}
              height="78px"
              onClick={() => {
                chooseRoute(route.path);
              }}
              align="flex-start"
              justify="center"
              onMouseEnter={() => setSelectedRouteIndex(index)}
            >
              <HStack align="flex-end">
                <Icon name={route.icon as IconName} size="L" />
                <VStack align="stretch" spacing={0}>
                  {route.parent ? (
                    <Text fontSize="s" textColor="gray.500">
                      {route.parent}
                    </Text>
                  ) : null}
                  <Text fontSize="xl">{route.name}</Text>
                </VStack>
              </HStack>
            </VStack>
          ))}
        </ModalBody>
      ) : null}
    </ModalContent>
  );
}

function addKeyDownListener(onKeyDown: (event: KeyboardEvent) => void) {
  document.addEventListener('keydown', onKeyDown, { capture: true });
  return () => {
    document.removeEventListener('keydown', onKeyDown, { capture: true });
  };
}
